/**
 * Wzorcowy Plan Kont (WPK) dla jednostek samorządu terytorialnego — gmin.
 * Źródło: Rozporządzenie Ministra Rozwoju i Finansów z 13 września 2017 r.
 * w sprawie rachunkowości oraz planów kont (Dz.U. 2017 poz. 1911):
 *  - załącznik nr 2 — plan kont dla budżetu JST (organ),
 *  - załącznik nr 3 — plan kont dla jednostek budżetowych i samorządowych
 *    zakładów budżetowych (zespoły 0–8).
 *
 * Każdy element poniżej korzenia ma 4 atrybuty konta (`attrs`):
 *  syntetyczne / analityczne / zbiorcze / bilansowe.
 */

export interface IWpkAccountAttrs {
  /** Konto syntetyczne (3-cyfrowe konto planu). */
  syntetyczne: boolean;
  /** Konto analityczne (rozwinięcie syntetyki). */
  analityczne: boolean;
  /** Konto zbiorcze (agregujące). */
  zbiorcze: boolean;
  /** Konto bilansowe (vs pozabilansowe). */
  bilansowe: boolean;
}

/** Karta charakterystyki konta — konfigurowana ręcznie przez użytkownika. */
export interface IWpkCharacteristics {
  /** Czy konto bilansowe (aktywa / pasywa / aktywo-pasywne). */
  bilansowe: boolean;
  typBilansowy: "aktywa" | "pasywa" | "aktywoPasywne" | null;
  /** Czy konto wynikowe (przychody / koszty). */
  wynikowe: boolean;
  typWynikowy: "przychody" | "koszty" | null;
  /** Dodatkowe uwagi / opis do konta. */
  uwagi: string;
}

export const DEFAULT_CHARACTERISTICS: IWpkCharacteristics = {
  bilansowe: false,
  typBilansowy: null,
  wynikowe: false,
  typWynikowy: null,
  uwagi: "",
};

export interface IWpkNode {
  id: string;
  code: string;
  label: string;
  /** Atrybuty konta — dla elementów poniżej korzenia. */
  attrs?: IWpkAccountAttrs;
  /** Karta charakterystyki konta — uzupełniana przez użytkownika. */
  characteristics?: IWpkCharacteristics;
  /** Element dodany przez użytkownika (poza wzorcem). */
  custom?: boolean;
  children: IWpkNode[];
}

// Konto syntetyczne wzorca (domyślne atrybuty).
const acc = (
  prefix: string,
  code: string,
  label: string,
  bilansowe = true,
): IWpkNode => ({
  id: `${prefix}-${code}`,
  code,
  label,
  attrs: { syntetyczne: true, analityczne: false, zbiorcze: false, bilansowe },
  children: [],
});

const grp = (
  id: string,
  code: string,
  label: string,
  children: IWpkNode[],
): IWpkNode => ({ id, code, label, children });

export const WPK_GMINY: IWpkNode[] = [
  grp("jb", "JB", "Jednostki budżetowe i samorządowe zakłady budżetowe", [
    grp("jb-z0", "0", "Zespół 0 – Aktywa trwałe", [
      acc("jb", "011", "Środki trwałe"),
      acc("jb", "013", "Pozostałe środki trwałe"),
      acc("jb", "014", "Zbiory biblioteczne"),
      acc("jb", "015", "Mienie zlikwidowanych jednostek"),
      acc("jb", "016", "Dobra kultury"),
      acc("jb", "017", "Sprzęt wojskowy"),
      acc("jb", "020", "Wartości niematerialne i prawne"),
      acc("jb", "030", "Długoterminowe aktywa finansowe"),
      acc("jb", "071", "Umorzenie środków trwałych oraz wartości niematerialnych i prawnych"),
      acc("jb", "072", "Umorzenie pozostałych środków trwałych, wartości niematerialnych i prawnych oraz zbiorów bibliotecznych"),
      acc("jb", "073", "Odpisy aktualizujące długoterminowe aktywa finansowe"),
      acc("jb", "077", "Umorzenie sprzętu wojskowego"),
      acc("jb", "080", "Środki trwałe w budowie (inwestycje)"),
    ]),
    grp("jb-z1", "1", "Zespół 1 – Środki pieniężne i rachunki bankowe", [
      acc("jb", "101", "Kasa"),
      acc("jb", "130", "Rachunek bieżący jednostki"),
      acc("jb", "131", "Rachunek bieżący samorządowych zakładów budżetowych"),
      acc("jb", "132", "Rachunek dochodów jednostek budżetowych"),
      acc("jb", "134", "Kredyty bankowe"),
      acc("jb", "135", "Rachunek środków funduszy specjalnego przeznaczenia"),
      acc("jb", "136", "Rachunek państwowych funduszy celowych"),
      acc("jb", "137", "Rachunek środków pochodzących ze źródeł zagranicznych niepodlegających zwrotowi"),
      acc("jb", "138", "Rachunek środków europejskich"),
      acc("jb", "139", "Inne rachunki bankowe"),
      acc("jb", "140", "Krótkoterminowe aktywa finansowe"),
      acc("jb", "141", "Środki pieniężne w drodze"),
    ]),
    grp("jb-z2", "2", "Zespół 2 – Rozrachunki i rozliczenia", [
      acc("jb", "201", "Rozrachunki z odbiorcami i dostawcami"),
      acc("jb", "221", "Należności z tytułu dochodów budżetowych"),
      acc("jb", "222", "Rozliczenie dochodów budżetowych"),
      acc("jb", "223", "Rozliczenie wydatków budżetowych"),
      acc("jb", "224", "Rozliczenie dotacji budżetowych oraz płatności z budżetu środków europejskich"),
      acc("jb", "225", "Rozrachunki z budżetami"),
      acc("jb", "226", "Długoterminowe należności budżetowe"),
      acc("jb", "227", "Rozliczenie wydatków z budżetu środków europejskich"),
      acc("jb", "228", "Rozliczenie środków pochodzących ze źródeł zagranicznych niepodlegających zwrotowi"),
      acc("jb", "229", "Pozostałe rozrachunki publicznoprawne"),
      acc("jb", "230", "Rozliczenia z budżetem środków europejskich"),
      acc("jb", "231", "Rozrachunki z tytułu wynagrodzeń"),
      acc("jb", "234", "Pozostałe rozrachunki z pracownikami"),
      acc("jb", "235", "Rozliczenia dochodów budżetowych z tytułu podatków"),
      acc("jb", "240", "Pozostałe rozrachunki"),
      acc("jb", "245", "Wpływy do wyjaśnienia"),
      acc("jb", "290", "Odpisy aktualizujące należności"),
    ]),
    grp("jb-z3", "3", "Zespół 3 – Materiały i towary", [
      acc("jb", "300", "Rozliczenie zakupu"),
      acc("jb", "310", "Materiały"),
      acc("jb", "330", "Towary"),
      acc("jb", "340", "Odchylenia od cen ewidencyjnych materiałów i towarów"),
    ]),
    grp("jb-z4", "4", "Zespół 4 – Koszty według rodzajów i ich rozliczenie", [
      acc("jb", "400", "Amortyzacja"),
      acc("jb", "401", "Zużycie materiałów i energii"),
      acc("jb", "402", "Usługi obce"),
      acc("jb", "403", "Podatki i opłaty"),
      acc("jb", "404", "Wynagrodzenia"),
      acc("jb", "405", "Ubezpieczenia społeczne i inne świadczenia"),
      acc("jb", "409", "Pozostałe koszty rodzajowe"),
      acc("jb", "490", "Rozliczenie kosztów"),
    ]),
    grp("jb-z5", "5", "Zespół 5 – Koszty według typów działalności i ich rozliczenie", [
      acc("jb", "500", "Koszty działalności podstawowej"),
      acc("jb", "530", "Koszty działalności pomocniczej"),
      acc("jb", "550", "Koszty zarządu"),
      acc("jb", "580", "Rozliczenie kosztów działalności"),
    ]),
    grp("jb-z6", "6", "Zespół 6 – Produkty", [
      acc("jb", "600", "Produkty gotowe i półfabrykaty"),
      acc("jb", "620", "Odchylenia od cen ewidencyjnych produktów"),
      acc("jb", "640", "Rozliczenia międzyokresowe kosztów"),
    ]),
    grp("jb-z7", "7", "Zespół 7 – Przychody, dochody i koszty", [
      acc("jb", "700", "Sprzedaż produktów i koszt ich wytworzenia"),
      acc("jb", "720", "Przychody z tytułu dochodów budżetowych"),
      acc("jb", "730", "Sprzedaż towarów i wartość ich zakupu"),
      acc("jb", "740", "Dotacje i środki na inwestycje"),
      acc("jb", "750", "Przychody finansowe"),
      acc("jb", "751", "Koszty finansowe"),
      acc("jb", "760", "Pozostałe przychody operacyjne"),
      acc("jb", "761", "Pozostałe koszty operacyjne"),
    ]),
    grp("jb-z8", "8", "Zespół 8 – Fundusze, rezerwy i wynik finansowy", [
      acc("jb", "800", "Fundusz jednostki"),
      acc("jb", "810", "Dotacje budżetowe, płatności z budżetu środków europejskich oraz środki z budżetu na inwestycje"),
      acc("jb", "820", "Rozliczenie wyniku finansowego"),
      acc("jb", "840", "Rezerwy i rozliczenia międzyokresowe przychodów"),
      acc("jb", "851", "Zakładowy fundusz świadczeń socjalnych"),
      acc("jb", "853", "Fundusze celowe"),
      acc("jb", "855", "Fundusz mienia zlikwidowanych jednostek"),
      acc("jb", "860", "Wynik finansowy"),
      acc("jb", "870", "Podatki i obowiązkowe rozliczenia z budżetem obciążające wynik finansowy"),
    ]),
  ]),
  grp("budzet", "B", "Budżet jednostki samorządu terytorialnego (organ)", [
    grp("budzet-bil", "B-BIL", "Konta bilansowe", [
      acc("b", "133", "Rachunek budżetu"),
      acc("b", "134", "Kredyty bankowe"),
      acc("b", "135", "Rachunek środków na niewygasające wydatki"),
      acc("b", "140", "Środki pieniężne w drodze"),
      acc("b", "222", "Rozliczenie dochodów budżetowych"),
      acc("b", "223", "Rozliczenie wydatków budżetowych"),
      acc("b", "224", "Rozrachunki budżetu"),
      acc("b", "225", "Rozliczenie niewygasających wydatków"),
      acc("b", "240", "Pozostałe rozrachunki"),
      acc("b", "250", "Należności finansowe"),
      acc("b", "260", "Zobowiązania finansowe"),
      acc("b", "290", "Odpisy aktualizujące należności"),
      acc("b", "901", "Dochody budżetu"),
      acc("b", "902", "Wydatki budżetu"),
      acc("b", "903", "Niewykonane wydatki"),
      acc("b", "904", "Niewygasające wydatki"),
      acc("b", "909", "Rozliczenia międzyokresowe"),
      acc("b", "960", "Skumulowane wyniki budżetu"),
      acc("b", "961", "Wynik wykonania budżetu"),
      acc("b", "962", "Wynik na pozostałych operacjach"),
      acc("b", "968", "Prywatyzacja"),
    ]),
    grp("budzet-poz", "B-POZ", "Konta pozabilansowe", [
      acc("b", "991", "Planowane dochody budżetu", false),
      acc("b", "992", "Planowane wydatki budżetu", false),
      acc("b", "993", "Rozliczenia z innymi budżetami", false),
    ]),
  ]),
];

// ── Odczyt drzewa WPK (z localStorage; fallback do wzorca) + spłaszczenie ────
export const WPK_STORAGE_KEY = "wpk_gminy_tree_v1";

// Zwraca drzewo aktywnego planu kont wskazanego gwiazdką w Ustawienia → Słowniki → Plany kont.
// Jeśli żaden plan użytkownika nie jest wskazany — fallback na wzorzec gminy (dotychczasowe zachowanie).
export const loadWpkTree = (): IWpkNode[] => {
  try {
    const postingId = localStorage.getItem("wpk_posting_plan_v1");
    if (postingId) {
      const plansRaw = localStorage.getItem("wpk_user_plans_v1");
      if (plansRaw) {
        const plans = JSON.parse(plansRaw) as Array<{ id: string; tree: IWpkNode[] }>;
        const found = plans.find((p) => p.id === postingId);
        if (found) return found.tree;
      }
    }
    const raw = localStorage.getItem(WPK_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as IWpkNode[]) : WPK_GMINY;
  } catch {
    return WPK_GMINY;
  }
};

export interface IWpkFlat {
  id: string;
  code: string;
  label: string;
  path: string; // pełna ścieżka „Korzeń / … / Konto"
}

export const flattenWpk = (nodes: IWpkNode[], prefix = ""): IWpkFlat[] => {
  const out: IWpkFlat[] = [];
  for (const n of nodes) {
    const path = prefix ? `${prefix} / ${n.label}` : n.label;
    out.push({ id: n.id, code: n.code, label: n.label, path });
    if (n.children.length) out.push(...flattenWpk(n.children, path));
  }
  return out;
};
